import React from "react";
import Button from "./Button";

/**
 * EmptyState — Ocean Blue Design System
 *
 * Props:
 *  icon        : ReactNode   — illustration or icon (40×40 recommended)
 *  title       : string      — primary heading
 *  description : string      — supporting message
 *  action      : { label, onClick, variant? } — primary CTA
 *  secondaryAction: { label, onClick }        — optional secondary CTA
 *  size        : "sm" | "md" | "lg"
 *  variant     : "default" | "search" | "error" | "success"
 */

const ICONS = {
  default: (
    <svg className="w-12 h-12" fill="none" viewBox="0 0 64 64" stroke="currentColor" strokeWidth={1.5}>
      <rect x="8" y="16" width="48" height="36" rx="6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 26h48" strokeLinecap="round" />
      <path d="M20 38h8M20 44h16" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  search: (
    <svg className="w-12 h-12" fill="none" viewBox="0 0 64 64" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="28" cy="28" r="14" strokeLinecap="round" />
      <path d="M38 38l12 12" strokeLinecap="round" />
      <path d="M22 28h12M28 22v12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg className="w-12 h-12" fill="none" viewBox="0 0 64 64" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="32" cy="32" r="22" strokeLinecap="round" />
      <path d="M32 22v12M32 40v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  success: (
    <svg className="w-12 h-12" fill="none" viewBox="0 0 64 64" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="32" cy="32" r="22" strokeLinecap="round" />
      <path d="M20 34l8 8 16-18" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const VARIANT_COLORS = {
  default: "text-[#CBD5E1] bg-[#F8FAFC]",
  search:  "text-[#93C5FD] bg-[#EFF6FF]",
  error:   "text-[#FCA5A5] bg-[#FEF2F2]",
  success: "text-[#6EE7B7] bg-[#ECFDF5]",
};

const SIZES = {
  sm: { wrap: "py-8 px-6", iconWrap: "w-14 h-14", title: "text-sm", desc: "text-xs" },
  md: { wrap: "py-12 px-8", iconWrap: "w-16 h-16", title: "text-base", desc: "text-sm" },
  lg: { wrap: "py-16 px-10", iconWrap: "w-20 h-20", title: "text-lg", desc: "text-sm" },
};

const EmptyState = ({
  icon,
  title = "No data found",
  description,
  action,
  secondaryAction,
  size = "md",
  variant = "default",
  className = "",
}) => {
  const sz = SIZES[size] ?? SIZES.md;
  const variantColor = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  const defaultIcon = ICONS[variant] ?? ICONS.default;

  return (
    <div
      className={[
        "flex flex-col items-center justify-center text-center",
        sz.wrap,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      role="status"
    >
      {/* Icon circle */}
      <div
        className={[
          "flex items-center justify-center rounded-2xl mb-5",
          sz.iconWrap,
          variantColor,
        ].join(" ")}
      >
        {icon ?? defaultIcon}
      </div>

      {/* Text */}
      <h3
        className={[
          "font-semibold text-[#1E293B] mb-2",
          sz.title,
        ].join(" ")}
      >
        {title}
      </h3>
      {description && (
        <p
          className={[
            "text-[#94A3B8] max-w-xs leading-relaxed mb-6",
            sz.desc,
          ].join(" ")}
        >
          {description}
        </p>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {action && (
            <Button
              variant={action.variant ?? "primary"}
              size="sm"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
